<template>
  <div>
    <user-profile-form-modal v-if="userFromContext" />

    <content-placeholder v-if="fetching">
      <el-card>
        <p class="text text-75"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
      </el-card>
    </content-placeholder>

    <el-card v-else>

      <div slot="header">
        <img :src="getGravatar(user.email,'150')" class="thumbnail"> Profile Information
      </div>
      <div slot="actions">
        <el-button
          v-if="canManagePtoActions"
          :disabled="isPtoActionSubmitting"
          :loading="isPtoActionSubmitting"
          @click="handleSetInitialPto">
          Set Initial PTO
        </el-button>
        <el-button
          v-if="canManagePtoActions"
          :disabled="isPtoActionSubmitting"
          :loading="isPtoActionSubmitting"
          @click="handleReallocatePto">
          Reallocate PTO
        </el-button>
        <el-button @click="openFormModal" v-if="user.profile">Edit Profile</el-button>
        <el-button @click="openFormModal" v-if="!user.profile">Create Profile</el-button>
      </div>

      <el-form
        ref="userForm"
        label-width="120px"
        label-position="right"
        v-if="user.profile">

        <el-row :gutter="20" v-if="user.email">
          <el-col :span="12">
            <el-row :gutter="20" v-if="user.profile.first_name || user.profile.last_name">
              <el-col>
                <el-form-item label="Full Name:" class="el-form-item__text">
                  {{user.profile.first_name}} {{user.profile.last_name}}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.email">
              <el-col>
                <el-form-item label="Email:" class="el-form-item__text">
                  {{ user.email }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.profile.phone">
              <el-col>
                <el-form-item label="Phone:" class="el-form-item__text">
                  {{ user.profile.phone }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.profile.title">
              <el-col>
                <el-form-item label="Title:" class="el-form-item__text">
                  {{ user.profile.title }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.profile.starting_data">
              <el-col>
                <el-form-item label="Starting date:" class="el-form-item__text">
                  {{ user.profile.starting_data }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col>
                <el-form-item label="PTO/Vacation Balance:" class="el-form-item__text">
                  {{ user.remaining_pto_days || 0 }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col>
                <el-form-item label="Sickness/Wellness Balance:" class="el-form-item__text">
                  {{ user.remaining_sickness_days || 0 }}
                </el-form-item>
              </el-col>
            </el-row>
          </el-col>
          <el-col :span="12">
            <el-row :gutter="20" v-if="user.profile.address">
              <el-col>
                <el-form-item label="Address:" class="el-form-item__text">
                  {{ user.profile.address }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.profile.address2">
              <el-col>
                <el-form-item label="Address 2:" class="el-form-item__text">
                  {{ user.profile.address2 }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.profile.city">
              <el-col>
                <el-form-item label="City:" class="el-form-item__text">
                  {{ user.profile.city }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.profile.state">
              <el-col>
                <el-form-item label="State:" class="el-form-item__text">
                  {{ user.profile.state }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20" v-if="user.profile.zip">
              <el-col>
                <el-form-item label="Zip:" class="el-form-item__text">
                  {{ user.profile.zip }}
                </el-form-item>
              </el-col>
            </el-row>
          </el-col>
        </el-row>
      </el-form>

      <div class="not-found" v-else>
        <el-alert type="info" :closable="false" title="Users profile hasn't been created yet." center></el-alert>
      </div>
    </el-card>
    
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import avatar from '@/mixins/avatar';
  import UserProfileFormModal from '@/components/profile/UserProfileFormModal';
  import { showSuccessMessage, showErrorMessage } from '@/ui/message';

  export default {
    name: 'UserProfile',
    props: {
      userId: {
        required: true,
        type: Number,
      },
    },
    mixins: [avatar],
    components: {
      userProfileFormModal: UserProfileFormModal,
    },
    data() {
      return {
        isPtoActionSubmitting: false,
      };
    },
    created() {
      this.fetchSingleUser({ id: this.userId })
        .then((response) => {
          if (response.status) {
            this.setContext(this.userId);
          }
        });
    },
    computed: {
      ...mapGetters('entities/users', ['fetching', 'userFromContext', 'currentUser']),

      canManagePtoActions() {
        const isAdmin = !!(this.currentUser
          && this.currentUser.permissions
          && this.currentUser.permissions.includes('admin'));
        const inSameCompany = !!(this.currentUser
          && this.user
          && this.currentUser.company_id
          && this.currentUser.company_id === this.user.company_id);

        return isAdmin && inSameCompany;
      },

      user() {
        return this.$store.getters['entities/users/query']()
          .with('profile')
          .with('user_memberships')
          .where('id', this.userId)
          .first();
      },
    },
    methods: {
      ...mapActions('entities/users', ['fetchSingleUser', 'setContext', 'allocateInitialTimeoff', 'reallocatePto']),
      ...mapActions('entities/profiles', ['openFormModal']),

      handleSetInitialPto() {
        this.$confirm('Set initial PTO for this user?', 'Confirm', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          this.isPtoActionSubmitting = true;

          return this.allocateInitialTimeoff(this.userId)
            .then((response) => {
              if (response.status) {
                return this.fetchSingleUser({ id: this.userId })
                  .then(() => {
                    showSuccessMessage('Initial PTO set');
                  });
              }

              showErrorMessage(response.error);
              return response;
            })
            .finally(() => {
              this.isPtoActionSubmitting = false;
            });
        })
          .catch(() => {});
      },

      handleReallocatePto() {
        this.$confirm('Reallocate PTO for this user?', 'Confirm', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          this.isPtoActionSubmitting = true;

          return this.reallocatePto(this.userId)
            .then((response) => {
              if (response.status) {
                return this.fetchSingleUser({ id: this.userId })
                  .then(() => {
                    showSuccessMessage('PTO reallocated');
                  });
              }

              showErrorMessage(response.error);
              return response;
            })
            .finally(() => {
              this.isPtoActionSubmitting = false;
            });
        })
          .catch(() => {});
      },
    },

  };
</script>
<style lang="scss">
.thumbnail{
  border-radius: 10%;
  vertical-align: middle;
}

.el-form-item__text .el-form-item__label,
.el-form-item__text .el-form-item__content{
  line-height: 10px;
}
</style>


// WEBPACK FOOTER //
// src/components/profile/UserProfile.vue